'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale } from 'next-intl';
import { getCurrencyByCode } from '@/config/currencies';
import { useExchangeRates } from '@/providers/exchange-rate-provider';
import { useCurrencyStore } from '@/stores/currency';
import { type FormatMode, formatBalance as rawFormatBalance, formatD6 as rawFormatD6, formatD6Signed as rawFormatD6Signed, formatD9 as rawFormatD9, formatMarketCapAxis as rawFormatMarketCapAxis, formatPrice as rawFormatPrice, formatPriceFixed as rawFormatPriceFixed, formatQuickAmount as rawFormatQuickAmount } from './format';
export type { FormatMode };
interface FormattedCurrencyResult {
  formatD6: (value: string, mode?: FormatMode) => string;
  /** Format an exact D9 outcome/share price without converting through Number. */
  formatD9: (value: string, mode?: FormatMode) => string;
  formatD6Signed: (value: string, mode?: FormatMode) => string;
  formatBalance: (value: number, mode?: FormatMode) => string;
  /** Format a Market Cap chart tick from the renderer's actual numeric step. */
  formatMarketCapAxis: (value: number, tickStep: number) => string;
  /** Format an outcome/share price (display-unit dollars) — rate + locale applied. */
  formatPrice: (value: number) => string;
  /** Format a price with an EXACT decimal count (chart y-axis tick alignment). */
  formatPriceFixed: (value: number, fractionDigits: number) => string;
  /** Format a quick-amount button label (value already in local currency). */
  formatQuickAmount: (value: number) => string;
  /** Format an already-local amount without compacting or applying FX again. */
  formatLocalAmount: (value: number) => string;
  /** Format a USDC amount for a local-currency number input. */
  formatInputAmount: (value: number) => string;
  currency: string;
  currencySymbol: string;
  isConverting: boolean;
  /** True when the selected currency has a live rate (USD always true). */
  hasRate: boolean;
  /** Active next-intl locale (drives grouping / symbol placement). */
  locale: string;
  /** USDC-to-local-currency exchange rate (1 for USD). */
  exchangeRate: number;
  /** Minor-unit decimal places of the active display currency (2 for USD/EUR, 0 for JPY). */
  currencyDecimals: number;
  /** Convert a local-currency amount back to USDC. */
  toUsdc: (localAmount: number) => number;
}
export function useFormattedCurrency() {
  const $ = _c(51);
  const currency = useCurrencyStore(_temp);
  const locale = useLocale();
  const {
    rates,
    isLoading
  } = useExchangeRates();
  let t0;
  if ($[0] !== currency) {
    t0 = getCurrencyByCode(currency);
    $[0] = currency;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const currencyInfo = t0;
  const symbol = currencyInfo?.symbol ?? "$";
  const isUsd = currency === "USD";
  const rate = isUsd ? 1 : Number(rates?.[currency] ?? 0);
  const hasRate = isUsd || rate > 0;
  const isConverting = !isUsd && isLoading;
  const effectiveCurrency = hasRate ? currency : "USD";
  const effectiveRate = hasRate ? rate : 1;
  let t1;
  if ($[2] !== currencyInfo || $[3] !== hasRate) {
    t1 = hasRate ? currencyInfo : getCurrencyByCode("USD");
    $[2] = currencyInfo;
    $[3] = hasRate;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const effectiveInfo = t1;
  const effectiveDecimals = effectiveInfo?.decimals ?? 2;
  let t2;
  if ($[5] !== effectiveCurrency || $[6] !== effectiveRate || $[7] !== locale) {
    t2 = {
      currency: effectiveCurrency,
      rate: effectiveRate,
      locale
    };
    $[5] = effectiveCurrency;
    $[6] = effectiveRate;
    $[7] = locale;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const opts = t2;
  let t3;
  if ($[9] !== opts) {
    t3 = function withMode(mode) {
      if (mode !== undefined) {
        return {
          ...opts,
          mode
        };
      }
      return opts;
    };
    $[9] = opts;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const withMode = t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[11] !== withMode) {
    t4 = (value, mode_0) => rawFormatD6(value, withMode(mode_0));
    t5 = (value_0, mode_1) => rawFormatD9(value_0, withMode(mode_1));
    t6 = (value_1, mode_2) => rawFormatD6Signed(value_1, withMode(mode_2));
    t7 = (value_2, mode_3) => rawFormatBalance(value_2, withMode(mode_3));
    $[11] = withMode;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
  }
  let t10;
  let t8;
  let t9;
  if ($[16] !== opts) {
    t8 = (value_3, tickStep) => rawFormatMarketCapAxis(value_3, tickStep, opts);
    t9 = value_4 => rawFormatPrice(value_4, opts);
    t10 = (value_5, fractionDigits) => rawFormatPriceFixed(value_5, fractionDigits, opts);
    $[16] = opts;
    $[17] = t10;
    $[18] = t8;
    $[19] = t9;
  } else {
    t10 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t11;
  if ($[20] !== effectiveCurrency || $[21] !== effectiveDecimals || $[22] !== locale) {
    t11 = value_6 => rawFormatQuickAmount(value_6, effectiveCurrency, effectiveDecimals, locale);
    $[20] = effectiveCurrency;
    $[21] = effectiveDecimals;
    $[22] = locale;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== effectiveCurrency || $[25] !== locale) {
    t12 = value_7 => rawFormatBalance(value_7, {
      currency: effectiveCurrency,
      rate: 1,
      locale,
      mode: "full"
    });
    $[24] = effectiveCurrency;
    $[25] = locale;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== effectiveDecimals || $[28] !== effectiveRate) {
    t13 = value_8 => effectiveRate === 1 ? String(value_8) : (value_8 * effectiveRate).toFixed(effectiveDecimals);
    $[27] = effectiveDecimals;
    $[28] = effectiveRate;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const t14 = hasRate ? symbol : "$";
  let t15;
  if ($[30] !== effectiveRate) {
    t15 = localAmount => {
      if (effectiveRate <= 0) {
        return localAmount;
      }
      return localAmount / effectiveRate;
    };
    $[30] = effectiveRate;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== effectiveCurrency || $[33] !== effectiveDecimals || $[34] !== effectiveRate || $[35] !== hasRate || $[36] !== isConverting || $[37] !== locale || $[38] !== t10 || $[39] !== t11 || $[40] !== t12 || $[41] !== t13 || $[42] !== t14 || $[43] !== t15 || $[44] !== t4 || $[45] !== t5 || $[46] !== t6 || $[47] !== t7 || $[48] !== t8 || $[49] !== t9) {
    t16 = {
      formatD6: t4,
      formatD9: t5,
      formatD6Signed: t6,
      formatBalance: t7,
      formatMarketCapAxis: t8,
      formatPrice: t9,
      formatPriceFixed: t10,
      formatQuickAmount: t11,
      formatLocalAmount: t12,
      formatInputAmount: t13,
      currency: effectiveCurrency,
      currencySymbol: t14,
      isConverting,
      hasRate,
      locale,
      exchangeRate: effectiveRate,
      currencyDecimals: effectiveDecimals,
      toUsdc: t15
    };
    $[32] = effectiveCurrency;
    $[33] = effectiveDecimals;
    $[34] = effectiveRate;
    $[35] = hasRate;
    $[36] = isConverting;
    $[37] = locale;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
    $[44] = t4;
    $[45] = t5;
    $[46] = t6;
    $[47] = t7;
    $[48] = t8;
    $[49] = t9;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  return t16;
}
function _temp(s) {
  return s.currency;
}